import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows transitioning between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * Transitionend handlers for slides currently exiting.
   * @type {WeakMap<HTMLElement, EventListener>}
   */
  #exitHandlers = new WeakMap();

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get sliderAnimation() {
    return this.dataset.sliderAnimation ?? 'slide';
  }

  get exitTransitionProperty() {
    return this.sliderAnimation === 'fade' ? 'opacity' : 'transform';
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];

    if (slides.length === 0) {
      this.#current = current;
      return;
    }

    let previousIndex = this.#current % slides.length;
    if (previousIndex < 0) {
      previousIndex += slides.length;
    }

    this.#current = current;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      if (index === relativeIndex) {
        this.#resetSlideExit(slide);
        slide.setAttribute('aria-hidden', 'false');
        return;
      }

      const wasCurrent = index === previousIndex && slide.getAttribute('aria-hidden') === 'false';

      slide.setAttribute('aria-hidden', 'true');

      if (wasCurrent) {
        this.#markSlideAsExiting(slide);
      } else {
        this.#resetSlideExit(slide);
      }
    });
  }

  /**
   * Marks a slide as exiting until its selected transition completes.
   * @param {HTMLElement} slide
   */
  #markSlideAsExiting(slide) {
    this.#resetSlideExit(slide);

    const transitionProperty = this.exitTransitionProperty;

    const handler = (event) => {
      if (event.target !== slide || event.propertyName !== transitionProperty) return;

      this.#resetSlideExit(slide);
    };

    this.#exitHandlers.set(slide, handler);
    slide.addEventListener('transitionend', handler);
    slide.setAttribute('data-exiting', 'true');
  }

  /**
   * Clears any pending exit state from a slide.
   * @param {HTMLElement} slide
   */
  #resetSlideExit(slide) {
    const handler = this.#exitHandlers.get(slide);

    if (handler) {
      slide.removeEventListener('transitionend', handler);
      this.#exitHandlers.delete(slide);
    }

    slide.removeAttribute('data-exiting');
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
